import React, { useState, useEffect } from 'react';
import { getUnassignedTransactions, getUnassignedCryptoTransactions, assignTransactionToCustomer, assignCryptoTransactionToCustomer, assignBlockchainTransactionToCustomer, searchCustomers } from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import { formatDate, formatCurrency, formatCryptoAmount } from '../utils/helpers';
import TxHashLink from './TxHashLink';
import { AddressLabel, useAddressLabels } from './AddressLabel';
import { counterpartyGate } from '../utils/counterpartyGate';
import { isDust } from '../utils/dust';
import NoticeClaimChip from './NoticeClaimChip';
import LinkReceiptsField from './LinkReceiptsField';
import { noticeHeldByOther, noticeLockedReason } from '../utils/noticeClaim';
import './AssignTransactionsModal.css';

// Dust / spam is never assignable to a customer. WHAT is dust is the server's
// flag (row.is_dust — audit T14; on-chain rows only, so manual entries are never
// dust): the endpoint already leaves dust out, and this filter keeps the list
// honest if it ever serves one. No amount threshold on the client.

function AssignTransactionsModal({ isOpen, onClose, user, preSelectedTransaction }) {
  // Shared address-label resolver — FALLBACK for the counterparty gate on rows
  // the picker endpoint did not project (see utils/counterpartyGate).
  const { resolve: resolveLabel } = useAddressLabels();
  const [activeTab, setActiveTab] = useState('money');
  const [moneyTransactions, setMoneyTransactions] = useState([]);
  const [cryptoTransactions, setCryptoTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // The customer's receipt(s) for a bank credit being linked (operator rule
  // 2026-10-29: compulsory for sales). Cleared whenever the selection changes.
  const [linkReceipts, setLinkReceipts] = useState([]);
  const [receiptError, setReceiptError] = useState('');
  useEffect(() => { setLinkReceipts([]); setReceiptError(''); }, [selectedTransaction]);
  const receiptRequired = activeTab === 'money' && selectedTransaction?.transaction_type === 'credit' && user?.role === 'sales';

  useEffect(() => {
    if (isOpen) {
      fetchUnassignedTransactions();
    }
  }, [isOpen]);

  // Handle pre-selected transaction from navigation.
  // The crypto tab combines two underlying tables (crypto_transactions and
  // blockchain_transactions) keyed by `${source}-${id}`, so for crypto/
  // blockchain we MUST match BOTH id and source — the same numeric id can
  // exist in both tables and refer to entirely different transactions.
  useEffect(() => {
    if (!preSelectedTransaction || !isOpen) return;

    const src = preSelectedTransaction.source;
    if (src === 'money') {
      setActiveTab('money');
      const found = moneyTransactions.find(t => t.id === preSelectedTransaction.id);
      // Someone else's notice (utils/noticeClaim): the server 409s the assign,
      // so leave it unselected — the row renders with who holds it.
      if (found && !noticeHeldByOther(found, user)) setSelectedTransaction(found);
    } else if (src === 'crypto' || src === 'blockchain') {
      setActiveTab('crypto');
      const found = cryptoTransactions.find(
        t => t.id === preSelectedTransaction.id && t.source === src
      );
      // Deep-link from a pending task can land on a row the counterparty rule
      // blocks. Selecting it would offer an Assign the server 409s, so leave it
      // unselected — the row still renders in the list with its reason.
      if (found && !counterpartyGate(found, resolveLabel).blocked && !noticeHeldByOther(found, user)) setSelectedTransaction(found);
    } else {
      // Unknown/legacy source — fall back to crypto tab, no auto-select.
      setActiveTab('crypto');
    }
  }, [preSelectedTransaction, isOpen, moneyTransactions, cryptoTransactions, resolveLabel, user]);

  const fetchUnassignedTransactions = async () => {
    setLoading(true);
    try {
      const [money, crypto] = await Promise.all([
        getUnassignedTransactions(),
        getUnassignedCryptoTransactions()
      ]);
      setMoneyTransactions(money || []);
      setCryptoTransactions(crypto || []);
    } catch (err) {
      console.error('Error fetching unassigned transactions:', err);
      showError('Failed to load unassigned transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSearch = async (query) => {
    setCustomerSearch(query);
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const results = await searchCustomers(query);
      setCustomerResults(results || []);
    } catch (err) {
      console.error('Error searching customers:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAssign = async (customerId, customerName) => {
    if (!selectedTransaction) return;
    if (receiptRequired && linkReceipts.length === 0) {
      setReceiptError("Upload the customer's payment receipt first — it is compulsory when linking a payment finance has already recorded.");
      return;
    }

    setAssigning(true);
    try {
      if (activeTab === 'money') {
        await assignTransactionToCustomer(selectedTransaction.id, customerId, { receiptUrls: linkReceipts });
        setMoneyTransactions(prev => prev.filter(t => t.id !== selectedTransaction.id));
      } else {
        // Check if it's a blockchain transaction or crypto transaction
        if (selectedTransaction.source === 'blockchain') {
          await assignBlockchainTransactionToCustomer(selectedTransaction.id, customerId);
        } else {
          await assignCryptoTransactionToCustomer(selectedTransaction.id, customerId);
        }
        setCryptoTransactions(prev => prev.filter(t => t.id !== selectedTransaction.id));
      }
      
      // The originating pr_notification task is NOT completed from here.
      // "Is this task done?" has exactly ONE source: the server-side derived
      // clause PR_NOTIFICATION_NOT_HIDDEN_BY_ACTION, which hides the task while
      // the underlying transaction is actioned (assigned / swapped / classified
      // / order-consumed / dust) and RE-SURFACES it automatically if that action
      // is reverted. Writing status='completed' here defeated the self-healing:
      // unlink the customer afterwards and the money went back to unattributed
      // with nothing left to nag anyone. (It also only ever fired for admins —
      // POST /api/tasks/:id/complete is finance/admin-gated, so the sales users
      // this modal is mostly for already relied on the derived rule.)
      // See the design note above PR_NOTIFICATION_NOT_HIDDEN_BY_ACTION.

      showSuccess(`Transaction assigned to ${customerName}`);
      setSelectedTransaction(null);
      setCustomerSearch('');
      setCustomerResults([]);
    } catch (err) {
      console.error('Error assigning transaction:', err);
      showError(err.response?.data?.error || 'Failed to assign transaction');
      // Stale snapshot (someone claimed / consumed it meanwhile) → refetch so
      // the list shows the real state instead of re-offering the row.
      if (err.response?.status === 409) { setSelectedTransaction(null); fetchUnassignedTransactions(); }
    } finally {
      setAssigning(false);
    }
  };

  // Using centralized formatDate from helpers.js

  if (!isOpen) return null;

  // Count only ASSIGNABLE crypto (dust is hidden + non-assignable), so the tab
  // badge matches the list instead of including hidden dust.
  const assignableCryptoCount = cryptoTransactions.filter(t => !isDust(t)).length;
  const totalUnassigned = moneyTransactions.length + assignableCryptoCount;

  return (
    <div className="modal-overlay">
      <div className="modal-content assign-tx-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Assign Transactions to Customers</h3>
          <button onClick={onClose} className="close-button">&times;</button>
        </div>

        <div className="modal-body">
          {/* Tabs */}
          <div className="tx-tabs">
            <button 
              className={`tx-tab ${activeTab === 'money' ? 'active' : ''}`}
              onClick={() => { setActiveTab('money'); setSelectedTransaction(null); }}
            >
              💵 Money ({moneyTransactions.length})
            </button>
            <button 
              className={`tx-tab ${activeTab === 'crypto' ? 'active' : ''}`}
              onClick={() => { setActiveTab('crypto'); setSelectedTransaction(null); }}
            >
              ₿ Crypto ({assignableCryptoCount})
            </button>
          </div>

          {loading ? (
            <div className="loading-message">Loading transactions...</div>
          ) : (
            <div className="tx-content">
              {/* Transaction List */}
              <div className="tx-list-section">
                <h4>Unassigned Transactions</h4>
                
                {activeTab === 'money' ? (
                  moneyTransactions.length > 0 ? (
                    <div className="tx-list">
                      {moneyTransactions.map(tx => {
                        // Someone else's notice → not selectable, says who holds it.
                        const noticeLocked = noticeHeldByOther(tx, user);
                        return (
                        <div 
                          key={tx.id} 
                          className={`tx-item ${noticeLocked ? 'tx-item--blocked' : ''} ${!noticeLocked && selectedTransaction?.id === tx.id ? 'selected' : ''}`}
                          title={noticeLocked ? noticeLockedReason(tx) : undefined}
                          onClick={() => { if (!noticeLocked) setSelectedTransaction(tx); }}
                        >
                          <div className="tx-main">
                            <div className="tx-bank">{tx.bank_name || 'Unknown'}</div>
                            <div className="tx-account">{tx.account_name}</div>
                          </div>
                          <div className="tx-details">
                            <div className={`tx-amount ${tx.transaction_type}`}>
                              {tx.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}
                            </div>
                            <div className="tx-date">{formatDate(tx.created_at)}</div>
                          </div>
                          {tx.description && <div className="tx-desc">{tx.description}</div>}
                          <NoticeClaimChip row={tx} user={user} />
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="no-tx">No unassigned money transactions</p>
                  )
                ) : (
                  (() => {
                  const assignableCrypto = cryptoTransactions.filter(tx => !isDust(tx));
                  const hiddenDust = cryptoTransactions.length - assignableCrypto.length;
                  return assignableCrypto.length > 0 ? (
                    <div className="tx-list">
                      {assignableCrypto.map(tx => {
                        // OPERATOR RULE (2026-08-10): an on-chain row whose
                        // counterparty is anonymous cannot be linked to a
                        // customer. The server 409s it (it would only surface
                        // as an error toast), so the row is not selectable and
                        // says why. Remedy lives on Blockchain Monitor /
                        // Customer Management (Save address).
                        const cp = counterpartyGate(tx, resolveLabel);
                        // Someone else's notice (utils/noticeClaim) — not selectable either.
                        const noticeLocked = noticeHeldByOther(tx, user);
                        const blocked = cp.blocked || noticeLocked;
                        return (
                        <div
                          key={`${tx.source}-${tx.id}`}
                          className={`tx-item ${blocked ? 'tx-item--blocked' : ''} ${!blocked && selectedTransaction?.id === tx.id && selectedTransaction?.source === tx.source ? 'selected' : ''}`}
                          title={noticeLocked ? noticeLockedReason(tx) : undefined}
                          onClick={() => { if (!blocked) setSelectedTransaction(tx); }}
                        >
                          <div className="tx-main">
                            <div className="tx-bank">
                              {tx.crypto_type}
                              {tx.source === 'blockchain' && (
                                <span className="source-badge blockchain">Blockchain</span>
                              )}
                            </div>
                            <div className="tx-account">{tx.group_name || tx.subaccount_name || 'Manual'}</div>
                          </div>
                          <div className="tx-details">
                            <div className={`tx-amount ${tx.transaction_type}`}>
                              {tx.transaction_type === 'credit' ? '+' : '-'}{formatCryptoAmount(tx.amount)} {tx.crypto_type}
                            </div>
                            <div className="tx-date">{formatDate(tx.created_at)}</div>
                          </div>
                          {/* Counterparty address — shows its SAVED name tag (or
                              public tag / OFAC badge) so a row is identifiable at
                              a glance, not just by amount + hash. */}
                          {(tx.transaction_type === 'credit' ? tx.from_address : tx.to_address) && (
                            <div className="tx-desc" style={{ fontSize: '0.75rem' }}>
                              {tx.transaction_type === 'credit' ? 'From: ' : 'To: '}
                              <AddressLabel address={tx.transaction_type === 'credit' ? tx.from_address : tx.to_address} />
                            </div>
                          )}
                          {tx.tx_hash && (
                            <div className="tx-desc" style={{ fontSize: '0.75rem' }}>
                              TX: <TxHashLink hash={tx.tx_hash} chain={tx.chain} truncate={10} />
                            </div>
                          )}
                          <NoticeClaimChip row={tx} user={user} />
                          {cp.blocked && (
                            <div className="tx-desc counterparty-block__reason" title={cp.message}>
                              🚫 Anonymous counterparty — label this address (Blockchain Monitor → Save) before linking it to a customer
                            </div>
                          )}
                        </div>
                        );
                      })}
                      {hiddenDust > 0 && (
                        <p className="no-tx" style={{ fontSize: '0.72rem', opacity: 0.65 }}>
                          {hiddenDust} dust transaction{hiddenDust > 1 ? 's' : ''} hidden — dust cannot be assigned
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="no-tx">No unassigned crypto transactions{hiddenDust > 0 ? ` (${hiddenDust} dust hidden)` : ''}</p>
                  );
                  })()
                )}
              </div>

              {/* Customer Selection */}
              {selectedTransaction && (
                <div className="customer-select-section">
                  <h4>Assign to Customer</h4>
                  <div className="selected-tx-summary">
                    <strong>Selected:</strong> 
                    {activeTab === 'money' 
                      ? `${formatCurrency(selectedTransaction.amount)} (${selectedTransaction.transaction_type})`
                      : `${formatCryptoAmount(selectedTransaction.amount)} ${selectedTransaction.crypto_type} (${selectedTransaction.transaction_type})`
                    }
                  </div>

                  {activeTab === 'money' && selectedTransaction.transaction_type === 'credit' && (
                    <LinkReceiptsField
                      urls={linkReceipts}
                      onChange={(next) => { setLinkReceipts(next); setReceiptError(''); }}
                      required={receiptRequired}
                      error={receiptError}
                    />
                  )}

                  <div className="customer-search">
                    <input
                      type="text"
                      placeholder="Search customer by name or number..."
                      value={customerSearch}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      className="form-control"
                    />

                    {searchLoading ? (
                      <div className="customer-results-dropdown search-loading">Searching...</div>
                    ) : customerResults.length > 0 ? (
                      <div className="customer-results-dropdown customer-results">
                        {customerResults.map(customer => (
                          <button
                            key={customer.id}
                            className="customer-result-btn"
                            onClick={() => handleAssign(customer.id, customer.customer_name)}
                            disabled={assigning}
                          >
                            <div className="customer-info">
                              <div className="customer-name">{customer.customer_name}</div>
                              <div className="customer-number">{customer.customer_number}</div>
                            </div>
                            <span className="assign-icon">→</span>
                          </button>
                        ))}
                      </div>
                    ) : customerSearch.length >= 2 ? (
                      <p className="customer-results-dropdown no-results">No customers found</p>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}

          {totalUnassigned === 0 && !loading && (
            <div className="all-assigned-message">
              ✓ All transactions have been assigned to customers
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssignTransactionsModal;

